import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import { fetchLeadsBySourcePie } from '../lib/crm/fetchLeadsBySourcePie';
import { getBusinessLeadCreatedAtRanges } from '../lib/crm/leadCreatedAtRanges';
import type { LeadsBySourceReportType } from '../lib/crm/leadsBySourceReport';
import type { SourcePieChartPayload } from '../lib/crm/sourcePieChart';

const EMPTY: SourcePieChartPayload = { total: 0, sourceDetails: [], chartData: null };

export function useCrmLeadsBySourcePie(reportType: LeadsBySourceReportType) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timezone =
    selectedBusiness?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'UTC';

  const [data, setData] = useState<SourcePieChartPayload>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setData(EMPTY);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      const { monthStart, nextMonthStart } = getBusinessLeadCreatedAtRanges(timezone);
      const result = await fetchLeadsBySourcePie(
        supabase,
        businessId,
        reportType,
        monthStart,
        nextMonthStart,
      );
      setData(result);
      setError(null);
    } catch (err) {
      console.error('Error loading leads by source:', err);
      setData(EMPTY);
      setError('Could not load report');
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId, timezone, reportType]);

  useEffect(() => {
    setLoading(true);
    void load();
    const interval = setInterval(() => void load(), 60_000);
    return () => clearInterval(interval);
  }, [load]);

  return { data, loading, error, refresh: load };
}
